"use client";

import React from "react";
import Image from "next/image";
import Button from "../ui/Button";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

interface Step {
  id: number
  title: string
  image: string
  width?: number
  height?: number
  class?: string
}

const MeetOurExperts: React.FC = () => {
    const steps: Step[] = [
      {
        id: 1,
        title: "Candidature",
        image: "/assets/icons/envelope.svg",
        width: 40,
        height: 40,
        class: "w-[166px] lg:mt-3 md:mt-9 mt-8.5",
      },
      {
        id: 2,
        title: "Analyse du profil",
        image: "/assets/icons/verification.svg",
        width: 50.02,
        height: 50,
        class: "min-w-[224px] lg:ms-9 lg:mt-11 mt-12",
      },
      {
        id: 3,
        title: "Entretien pédagogique",
        image: "/assets/icons/characters-on-chairs.svg",
        width: 122,
        height: 80.46,
        class: "w-[182px] lg:mt-2 md:mt-8 mt-9",
      },
      {
        id: 4,
        title: "Mise en situation",
        image: "/assets/icons/scenario.svg",
        width: 59.74,
        height: 102,
        class: "w-[228px] min-w-[150px] lg:mt-7.5 mt-12",
      },
      {
        id: 5,
        title: "Validation & intégration",
        image: "/assets/icons/thumbs-up.svg",
        width: 65,
        height: 43.84,
        class: "w-[170px] min-w-[120px] xl:mt-5 lg:mt-4 md:mt-8 mt-9",
      },
    ];
    return (
        <section className="md:py-12 py-10 bg-white lg:px-4">
            <div className="max-w-314.5 mx-auto">
                <h2 className="lg:px-0 px-4 font-red-hat text-[30px] md:text-[40px] leading-[42.8px] md:leading-15.75 font-light text-[#1B365E] text-center md:mb-5.75">
                    Des mentors sélectionnés avec soin
                </h2>

                {/* Timeline Section */}
                <div className="relative lg:pt-6 pb-12 lg:ps-0 ps-4 overflow-x-auto hidden md:block">
                    {/* Dotted path background */}
                    <Image className="absolute inset-0 xl:w-[88.5%] lg:w-[86%] md:w-[80%] w-[640px] min-w-[640px] mx-auto h-full lg:-top-20.5 -top-19 md:left-0 left-15 pointer-events-none" src="/assets/blue-curly-line.svg" alt="Dotted Line" title="Dotted Line" width={1089} height={67.64} />
                    {/* Steps Grid - add min-w here to force horizontal width */}
                    <div className="flex xl:gap-4 gap-0 justify-between relative z-10 min-w-[753.76px]">
                    {steps.map((step) => (
                        <div key={step.id} className={`lg:last:me-0 last:me-4 relative flex flex-col items-center gap-2.75 ${step.class || ""}`}>
                        <div className="flex items-center justify-center w-7.5 h-7.5 shrink-0 text-xs leading-3.5 font-bold text-[#1B365E] rounded-full bg-[#C7F8FF] border-b border-r border-[#D9D9D9]">
                            {step.id}
                        </div>
                        {/* Image Container */}
                        <div className="flex justify-center">
                            <Image src={step.image || "/placeholder.svg"} alt={step.title} title={step.title} width={step.width ?? 198} height={step.height ?? 178} />
                        </div>

                        {/* Title */}
                        <h3 className="text-center font-light font-red-hat text-[#1B365E] text-[18px] lg:text-[24px] xl:text-[30px] leading-5 lg:leading-9.5 xl:leading-10 tracking-[0.03em] whitespace-pre-line">
                            {step.title}
                        </h3>

                        {step.id === 1 &&
                            <Image className="absolute lg:bottom-10 md:bottom-13 bottom-6.5 xl:left-49 lg:left-44 sm:left-[16vw] left-31" src="/assets/icons/men.svg" alt="Men" title="Men" width={46} height={91} />
                        }
                        </div>
                    ))}
                    </div>
                </div>
            </div>

            {/* Mobile Slider */}
            <div className="relative pt-6 lg:pt-6 pb-6 md:hidden block">
                <Swiper
                    modules={[Pagination]}
                    pagination={{ clickable: true }}
                    spaceBetween={0}
                    slidesPerView={'auto'}
                    className="experts-swiper relative z-10 !pb-7"
                >
                    <style jsx global>{`
                        .experts-swiper .swiper-wrapper {
                          display: flex !important;
                          align-items: stretch !important;
                          position: relative !important;
                        }
                        .experts-swiper .swiper-wrapper::before {
                          content: "";
                          position: absolute;
                          pointer-events: none;
                          z-index: 0;
                          background-image: url('/assets/blue-curly-line.svg');
                          background-repeat: no-repeat;
                          background-size: 800px auto;
                          background-position: left top;
                          width: 800px;
                          height: 100%;
                          left: 80px;
                          top: 35px;
                          transform: none;
                        }
                        .experts-swiper .swiper-slide {
                          position: relative !important;
                          z-index: 1 !important;
                          width: auto !important;
                          height: auto !important;
                          display: flex !important;
                          flex-direction: column !important;
                        }
                      `}</style>
                    {steps.map((step) => (
                        <SwiperSlide key={step.id} className="!w-auto">
                            <div className={`relative flex flex-col items-center gap-2.75 ${step.class || ""}`}>
                                <div className="flex items-center justify-center w-7.5 h-7.5 shrink-0 text-xs leading-3.5 font-bold text-[#1B365E] rounded-full bg-[#C7F8FF] border-b border-r border-[#D9D9D9]">
                                    {step.id}
                                </div>
                                <div className="flex justify-center">
                                    <Image src={step.image || "/placeholder.svg"} alt={step.title} title={step.title} width={step.width ?? 198} height={step.height ?? 178} />
                                </div>

                                <h3 className="text-center font-light font-red-hat text-[#1B365E] text-[18px] lg:text-[24px] xl:text-[30px] leading-5 lg:leading-9.5 xl:leading-10 tracking-[0.03em] whitespace-pre-line">
                                    {step.title}
                                </h3>

                                {step.id === 1 && (
                                    <Image className="absolute lg:bottom-10 md:bottom-13 -bottom-13 xl:left-49 lg:left-44 md:left-[16vw] left-44" src="/assets/icons/men.svg" alt="Men" title="Men" width={46} height={91} />
                                )}
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>

            <div className="max-w-310 mx-auto lg:px-0 px-4">
                <div className="flex flex-col lg:flex-row items-start gap-8 md:gap-9">
                    {/* Left Column: Static Image */}
                    <div className="w-full lg:w-126.75 shrink-0">
                        <div className='cursor-pointer relative w-full aspect-507/264 overflow-hidden'>
                            <Image
                                src="/assets/video-thumbnail-0.png"
                                alt="Pourquoi nous rejoindre"
                                fill
                                className='object-cover'
                                priority
                            />
                            <Image className="absolute top-1/2 left-1/2 transition-translate -translate-x-1/2 -translate-y-1/2" src="/assets/icons/play.svg" alt="Play" title="Play" width={40} height={40} />
                        </div>
                        <div className="flex gap-4.5 md:h-[61.21px] h-[51.82px] mt-4.5 md:mt-[32.54px]">
                            <button className="relative cursor-pointer">
                                <Image className="md:w-[128.91px] w-[109.13px] md:h-[61.21px] h-[51.82px] object-cover" src="/assets/video-thumbnail-1.png" alt="Video thumbnail 1" title="Video Thumbnail 1" width={128.91} height={61.21} objectFit="cover"/>
                                <Image className="absolute top-1/2 left-1/2 transition-translate -translate-x-1/2 -translate-y-1/2 w-[14.84px] h-[13.91px]" src="/assets/icons/play.svg" alt="Play" title="Play" width={40} height={40} />
                            </button>
                            <button className="relative cursor-pointer">
                                <Image className="md:w-[128.91px] w-[109.13px] md:h-[61.21px] h-[51.82px] object-cover" src="/assets/video-thumbnail-2.png" alt="Video thumbnail 1" title="Video Thumbnail 1" width={128.91} height={61.21} objectFit="cover"/>
                                <Image className="absolute top-1/2 left-1/2 transition-translate -translate-x-1/2 -translate-y-1/2 w-[14.84px] h-[13.91px]" src="/assets/icons/play.svg" alt="Play" title="Play" width={40} height={40} />
                            </button>
                            <button className="relative cursor-pointer">
                                <Image className="md:w-[128.91px] w-[109.13px] md:h-[61.21px] h-[51.82px] object-cover" src="/assets/video-thumbnail-3.png" alt="Video thumbnail 1" title="Video Thumbnail 1" width={128.91} height={61.21} objectFit="cover"/>
                                <Image className="absolute top-1/2 left-1/2 transition-translate -translate-x-1/2 -translate-y-1/2 w-[14.84px] h-[13.91px]" src="/assets/icons/play.svg" alt="Play" title="Play" width={40} height={40} />
                            </button>
                        </div>
                    </div>

                    {/* Right Column: Features */}
                    <div className="w-full lg:flex-1 space-y-6 sm:space-y-8 sm:mt-3.5">
                        <p className="text-sm leading-5 text-[#1D1D1D] md:text-left text-center">
                            Chez Spot Formations, nous sommes dans un <strong>processus d&apos;amélioration continue</strong>, et nous sommes convaincus que la <strong>qualité d&apos;une formation</strong> repose avant tout sur <strong>l&apos;expertise de ses formateurs</strong>. C&apos;est pourquoi nous avons réuni une équipe de professionnels chevronnés, experts dans leur domaine, dotés d&apos;une solide expérience pratique.
                            <br/><br/>
                            Au-delà de la transmission de connaissances, <strong>ils vous offrent un accompagnement personnalisé</strong>, un coaching individualisé et un soutien constant tout au long de votre parcours d&apos;apprentissage, vous permettant ainsi de développer pleinement votre potentiel et d&apos;atteindre vos objectifs professionnels.
                        </p>
                        <Button variant="outline-gradient" className="px-10 md:w-fit w-full py-4 text-[15px]">
                            Enseigner chez nous
                        </Button>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default MeetOurExperts;
